import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime } from 'luxon';
import { Autocomplete } from '@mui/material';
import { debounce } from 'lodash';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import { Organization } from 'utils';
import {
    Fp1Contract,
    Fp1ContractStatus,
    Fp1ContractDiscountType,
    ALL_CONTRACT_STATUSES,
    ALL_DISCOUNT_TYPES,
    CONTRACT_STATUS_LABELS,
    CONTRACT_DISCOUNT_TYPE_LABELS,
    CreateContractParams,
} from '../contracts.types';
import { createContract, updateContract } from '../contracts.api';

interface Props {
    open: boolean;
    contract: Fp1Contract | null;
    onClose: () => void;
    onSave: (contract: Fp1Contract) => void;
}

const BLANK: CreateContractParams = {
    contract_name: '',
    organization_uuid: null,
    organization_name: null,
    status: 'draft',
    start_date: null,
    expiration_date: null,
    renewal_reminder_date: null,
    auto_renew: false,
    internal_notes: null,
    custom_terms: null,
    discount_type: null,
    discount_value: null,
    max_devices: null,
    included_athletes: null,
    free_replacements_allowed: null,
};

export default function Fp1ContractFormDialog({ open, contract, onClose, onSave }: Props) {
    const isEdit = !!contract;

    const [form, setForm] = useState<CreateContractParams>(BLANK);
    const [statusChangeNotes, setStatusChangeNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Org autocomplete
    const [orgOptions, setOrgOptions] = useState<Organization[]>([]);
    const [orgInput, setOrgInput] = useState('');
    const [orgLoading, setOrgLoading] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

    useEffect(() => {
        if (!open) return;
        if (contract) {
            setForm({
                contract_name: contract.contract_name,
                organization_uuid: contract.organization_uuid,
                organization_name: contract.organization_name,
                status: contract.status,
                start_date: contract.start_date,
                expiration_date: contract.expiration_date,
                renewal_reminder_date: contract.renewal_reminder_date,
                auto_renew: contract.auto_renew,
                internal_notes: contract.internal_notes,
                custom_terms: contract.custom_terms,
                discount_type: contract.discount_type,
                discount_value: contract.discount_value,
                max_devices: contract.max_devices,
                included_athletes: contract.included_athletes,
                free_replacements_allowed: contract.free_replacements_allowed,
            });
            if (contract.organization_uuid && contract.organization_name) {
                setSelectedOrg({
                    uuid: contract.organization_uuid,
                    name: contract.organization_name,
                } as Organization);
                setOrgInput(contract.organization_name);
            }
        } else {
            setForm(BLANK);
            setSelectedOrg(null);
            setOrgInput('');
        }
        setStatusChangeNotes('');
        setErrors({});
    }, [open, contract]);

    const fetchOrgs = debounce((val: string) => {
        if (!val.trim()) return;
        setOrgLoading(true);
        adminGetAllOrganizations(val, 20)
            .then((res) => setOrgOptions((res.data as any)?.data ?? []))
            .catch(() => {})
            .finally(() => setOrgLoading(false));
    }, 350);

    const setField = <K extends keyof CreateContractParams>(
        key: K,
        val: CreateContractParams[K],
    ) => {
        setForm((f) => ({ ...f, [key]: val }));
        setErrors((e) => ({ ...e, [key]: '' }));
    };

    const parseDateField = (dt: DateTime | null): string | null =>
        dt && dt.isValid ? dt.toISODate() : null;

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        if (!form.contract_name.trim()) errs.contract_name = 'Contract name is required';
        if (!form.status) errs.status = 'Status is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            let res;
            if (isEdit && contract) {
                res = await updateContract(contract.uuid, {
                    ...form,
                    status_change_notes: statusChangeNotes || null,
                });
            } else {
                res = await createContract(form);
            }
            onSave((res.data as any).data ?? res.data);
        } catch (err: any) {
            const apiErrors = err?.response?.data?.errors ?? {};
            const mapped: Record<string, string> = {};
            Object.keys(apiErrors).forEach((k) => {
                mapped[k] = Array.isArray(apiErrors[k]) ? apiErrors[k][0] : apiErrors[k];
            });
            setErrors(mapped);
        } finally {
            setSaving(false);
        }
    };

    const SectionLabel = ({ children }: { children: React.ReactNode }) => (
        <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mt: 2, mb: 0.5 }}
        >
            {children}
        </Typography>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Contract' : 'Create Contract'}</DialogTitle>
            <DialogContent dividers>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* ── General ── */}
                        <SectionLabel>General</SectionLabel>

                        <TextField
                            label="Contract Name"
                            required
                            value={form.contract_name}
                            onChange={(e) => setField('contract_name', e.target.value)}
                            error={!!errors.contract_name}
                            helperText={errors.contract_name}
                            fullWidth
                            size="small"
                        />

                        <Autocomplete
                            freeSolo
                            options={orgOptions}
                            getOptionLabel={(o) => (typeof o === 'string' ? o : o.name ?? '')}
                            isOptionEqualToValue={(option, value) =>
                                typeof value === 'string'
                                    ? option.name === value
                                    : option.uuid === value.uuid
                            }
                            value={selectedOrg}
                            inputValue={orgInput}
                            loading={orgLoading}
                            onInputChange={(_, val, reason) => {
                                setOrgInput(val);
                                fetchOrgs(val);
                                if (reason === 'clear') {
                                    // User clicked the X — clear everything
                                    setSelectedOrg(null);
                                    setField('organization_uuid', null);
                                    setField('organization_name', null);
                                } else if (!selectedOrg) {
                                    // No org selected — treat typed text as free-form name
                                    setField('organization_name', val || null);
                                    setField('organization_uuid', null);
                                }
                            }}
                            onChange={(_, org) => {
                                if (typeof org === 'string') {
                                    // User committed free text (pressed Enter)
                                    setSelectedOrg(null);
                                    setField('organization_uuid', null);
                                    setField('organization_name', org || null);
                                } else {
                                    setSelectedOrg(org);
                                    setField('organization_uuid', org?.uuid ?? null);
                                    setField('organization_name', org?.name ?? null);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Organization"
                                    size="small"
                                    placeholder="Search or type a name"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {orgLoading && <CircularProgress size={14} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <FormControl size="small" fullWidth error={!!errors.status}>
                            <InputLabel required>Status</InputLabel>
                            <Select
                                value={form.status}
                                label="Status *"
                                onChange={(e) =>
                                    setField('status', e.target.value as Fp1ContractStatus)
                                }
                            >
                                {ALL_CONTRACT_STATUSES.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {CONTRACT_STATUS_LABELS[s]}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                        </FormControl>

                        {isEdit && form.status !== contract?.status && (
                            <TextField
                                label="Status Change Notes"
                                value={statusChangeNotes}
                                onChange={(e) => setStatusChangeNotes(e.target.value)}
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                placeholder="Reason for status change…"
                            />
                        )}

                        <TextField
                            label="Internal Notes"
                            value={form.internal_notes ?? ''}
                            onChange={(e) => setField('internal_notes', e.target.value || null)}
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                        />

                        <Divider />

                        {/* ── Dates ── */}
                        <SectionLabel>Dates</SectionLabel>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <DatePicker
                                label="Start Date"
                                value={form.start_date ? DateTime.fromISO(form.start_date) : null}
                                onChange={(dt) => setField('start_date', parseDateField(dt))}
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                            />
                            <DatePicker
                                label="Expiration Date"
                                value={
                                    form.expiration_date
                                        ? DateTime.fromISO(form.expiration_date)
                                        : null
                                }
                                onChange={(dt) => setField('expiration_date', parseDateField(dt))}
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                            />
                            <DatePicker
                                label="Renewal Reminder Date"
                                value={
                                    form.renewal_reminder_date
                                        ? DateTime.fromISO(form.renewal_reminder_date)
                                        : null
                                }
                                onChange={(dt) =>
                                    setField('renewal_reminder_date', parseDateField(dt))
                                }
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.auto_renew}
                                    onChange={(e) => setField('auto_renew', e.target.checked)}
                                />
                            }
                            label="Auto Renew"
                        />

                        <Divider />

                        {/* ── Pricing ── */}
                        <SectionLabel>Pricing Terms</SectionLabel>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Discount Type</InputLabel>
                                <Select
                                    value={form.discount_type ?? ''}
                                    label="Discount Type"
                                    onChange={(e) =>
                                        setField(
                                            'discount_type',
                                            (e.target.value as Fp1ContractDiscountType) || null,
                                        )
                                    }
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {ALL_DISCOUNT_TYPES.map((d) => (
                                        <MenuItem key={d} value={d}>
                                            {CONTRACT_DISCOUNT_TYPE_LABELS[d]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Discount Value"
                                type="number"
                                value={form.discount_value ?? ''}
                                onChange={(e) => setField('discount_value', e.target.value || null)}
                                size="small"
                                sx={{ minWidth: 160 }}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Box>

                        <Divider />

                        {/* ── Operational Terms ── */}
                        <SectionLabel>Operational Terms</SectionLabel>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                                label="Max Devices"
                                type="number"
                                value={form.max_devices ?? ''}
                                onChange={(e) =>
                                    setField(
                                        'max_devices',
                                        e.target.value ? Number(e.target.value) : null,
                                    )
                                }
                                size="small"
                                sx={{ minWidth: 140 }}
                                inputProps={{ min: 0 }}
                            />
                        </Box>

                        <TextField
                            label="Custom Terms"
                            value={form.custom_terms ?? ''}
                            onChange={(e) => setField('custom_terms', e.target.value || null)}
                            size="small"
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Any additional contractual terms…"
                        />
                    </Box>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} /> : undefined}
                >
                    {isEdit ? 'Save Changes' : 'Create Contract'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
